import { useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { ALL_VOWS } from "@/data/vows";
import type { useFocus as UseFocusType } from "@/hooks/useFocus";

const ZEHN_TUGENDEN = ALL_VOWS.filter((v) => v.category === "ten-virtues");

// Format local date to German DD. Monat YYYY
function fmtEndDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
}

interface FokusSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: ReturnType<typeof UseFocusType>["addFocus"];
}

export function FokusSheet({ open, onOpenChange, onSave }: FokusSheetProps) {
  const [text, setText] = useState("");
  const [days, setDays] = useState(7);
  const [virtueIds, setVirtueIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const toggleVirtue = useCallback((id: number) => {
    setVirtueIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!text.trim()) return;
    setSaving(true);
    await onSave({ text, durationDays: days, virtueIds });
    setSaving(false);
    // Reset + close
    setText("");
    setDays(7);
    setVirtueIds([]);
    onOpenChange(false);
  }, [text, days, virtueIds, onSave, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[85dvh] rounded-t-2xl border-t border-gray-200 dark:border-[#1E3A5F] p-0 bg-white dark:bg-[#0D1117] [&>button]:hidden"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-[#1E3A5F]" />
        </div>

        <ScrollArea className="h-[calc(85dvh-1.5rem)]">
          <div className="px-5 pt-3 pb-10 space-y-6">
            <h2 className="text-lg font-bold text-center text-gray-900 dark:text-white">
              Fokus setzen
            </h2>

            {/* Vorsatz */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Mein Vorsatz
              </label>
              <textarea
                className="w-full rounded-xl border border-gray-200 dark:border-[#1E3A5F] bg-gray-50 dark:bg-[#1E3A5F]/20 px-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-vajra-blue/40 resize-none"
                rows={3}
                placeholder="Was möchte ich in dieser Zeit üben oder verändern?"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>

            {/* Dauer — Stepper */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Dauer
              </label>
              <div className="flex items-center gap-0">
                <button
                  type="button"
                  onClick={() => setDays((d) => Math.max(1, d - 1))}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-l-xl border border-r-0 border-gray-200 dark:border-[#1E3A5F] bg-gray-50 dark:bg-[#1E3A5F]/30 text-vajra-blue dark:text-vajra-cyan text-xl font-bold"
                >
                  −
                </button>
                <div className="flex h-10 flex-1 items-center justify-center border border-gray-200 dark:border-[#1E3A5F] bg-white dark:bg-[#0D1117] text-base font-bold tabular-nums text-gray-900 dark:text-white">
                  {days} Tage
                </div>
                <button
                  type="button"
                  onClick={() => setDays((d) => Math.min(365, d + 1))}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-r-xl border border-l-0 border-gray-200 dark:border-[#1E3A5F] bg-gray-50 dark:bg-[#1E3A5F]/30 text-vajra-blue dark:text-vajra-cyan text-xl font-bold"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-center text-purple-500 dark:text-purple-400">
                bis {fmtEndDate(days)}
              </p>
            </div>

            {/* Zehn Tugenden */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Zehn Tugenden <span className="normal-case font-normal">(optional)</span>
              </label>
              <div className="space-y-1.5">
                {ZEHN_TUGENDEN.map((vow) => {
                  const checked = virtueIds.includes(vow.id);
                  return (
                    <button
                      key={vow.id}
                      type="button"
                      onClick={() => toggleVirtue(vow.id)}
                      className={cn(
                        "w-full flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                        checked
                          ? "border-purple-500/40 bg-purple-500/5 dark:bg-purple-500/10"
                          : "border-gray-200 dark:border-[#1E3A5F] hover:border-gray-300 dark:hover:border-gray-600"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                          checked
                            ? "border-purple-500 bg-purple-500"
                            : "border-gray-300 dark:border-gray-600"
                        )}
                      >
                        {checked && <Check className="h-2.5 w-2.5 text-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-medium leading-snug", checked ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300")}>
                          {vow.de.title}
                        </p>
                        {vow.de.positiveFormulation && (
                          <p className={cn("text-xs mt-0.5", checked ? "text-purple-600 dark:text-purple-400" : "text-muted-foreground")}>
                            {vow.de.positiveFormulation}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Save */}
            <Button
              className="w-full h-12 text-base font-semibold bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!text.trim() || saving}
              onClick={handleSave}
            >
              {saving ? "Wird gespeichert…" : "Fokus setzen"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
